import { MenuItem } from "@mui/material"
import { ReactElement, useState } from "react"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { ConfirmModal } from "../../../../../shared/components/dialogs/ConfirmModal"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { toDineroOrUndefined } from "../../../../currency/currencyLib"
import { useMoney } from "../../../../currency/useMoney"
import { Order } from "../../../controlKitchenTypes"
import { Receipt, ReceiptDialog } from "../../../../receipt/pages/ReceiptDialog"
import { refundOrder } from "../../../../receipt/ordersApi"
import { StandardSuccessDialog } from "../../../../../shared/components/dialogs/StandardSuccessDialog"
import {
    StandardErrorDialog,
    useStandardErrorDialogState,
} from "../../../../../shared/components/dialogs/StandardErrorDialog"
import { useLabelPrinter } from "../../../../serviceCenter/report/home/hooks/useLabelPrinter"
import { GuestData } from "../../../../serviceCenter/report/external/brotherPrinter"
import { LabelPrinterExtensionDialog } from "../../../../../shared/components/dialogs/LabelPrinterExtensionDialog"
import { ProductionOrderTypeFilter } from "../pages/components/PosTransactionsTable"
import { ApiError } from "../../../../../core/api/errorTypes"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"

type OrderActionMenuProps = Readonly<{
    order: Order
    orders?: Order[]
    onSuccess?: () => void
    closeMenu?: () => void
    orderType?: ProductionOrderTypeFilter
}>

export function OrderActionMenu({ order, orders, onSuccess, closeMenu, orderType }: OrderActionMenuProps) {
    const { l10n } = useAppLocalization()
    const moneyFactory = useMoney({})
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const { errorDialog: showErrorDialog, openErrorDialog, closeErrorDialog } = useStandardErrorDialogState()
    const [receipt, setReceipt] = useState<Receipt>()

    const { isInstalledError, clearState, printGuestLabel } = useLabelPrinter()

    const handlePrintLabel = () => {
        const labelData: GuestData = {
            visitorName: order.customer?.displayName || "",
            visitorCompany: order.customer?.userGroup?.name || (order as Order).accountingCustomerParty?.name || "",
            hostName: order.customer?.displayName || "",
        }
        printGuestLabel(labelData)
    }

    const { mutateAsync: callRefundOrder, isLoading: isRefunding } = useApiMutation(refundOrder, {
        async onSuccess(_params, _data) {
            setShowConfirmModal(false)
            setShowSuccessModal(true)
        },
        async onError(_, error) {
            setShowConfirmModal(false)
            const displayMessage = error instanceof ApiError && error.hasApiMessage ? error.displayMessage : undefined
            openErrorDialog({
                title: l10n.getString("transactions-table-list-refund"),
                description: displayMessage || l10n.getString("transactions-table-list-refund-error-message"),
            })
        },
    })

    const handleRefundClick = () => {
        setShowConfirmModal(true)
    }

    const handleConfirmRefund = () => {
        callRefundOrder({ orderId: order.id })
    }

    const handleShowReceipt = () => {
        setReceipt({
            orderId: order.id,
            orderUid: order.uid,
            orderType: order.orderType,
        })
    }

    const onPrinterClose = () => {
        clearState()
        closeMenu?.()
    }

    const amountDisplay = (() => {
        const dineroAmount = toDineroOrUndefined(order.amount)
        return dineroAmount ? moneyFactory.format(dineroAmount) : ""
    })()

    const menuItems: ReactElement[] = []

    menuItems.push(
        <LocalizedStrict id="office-purchase-receipt" key={`receipt-${order.id}`}>
            <MenuItem onClick={handleShowReceipt}>Receipt</MenuItem>
        </LocalizedStrict>,
    )

    if (order.orderAction?.refundOrder?.allow) {
        menuItems.push(
            <LocalizedStrict id="transactions-table-list-refund" key={`refund-${order.id}`}>
                <MenuItem onClick={handleRefundClick}>Refund</MenuItem>
            </LocalizedStrict>,
        )
    }

    if (orderType === ProductionOrderTypeFilter.TAKEAWAY) {
        menuItems.push(
            <LocalizedStrict id="floating-action-button-print-label" key={`print-label-${order.id}`}>
                <MenuItem onClick={handlePrintLabel}>Print label</MenuItem>
            </LocalizedStrict>,
        )
    }

    return (
        <>
            {menuItems}

            <LocalizedStrict
                id="transactions-table-list-refund"
                attrs={{ title: true, message: true, buttonLabelSubmit: true, buttonLabelCancel: true }}
                vars={{
                    orderId: order.id.toString(),
                    amount: amountDisplay,
                }}
            >
                <ConfirmModal
                    open={showConfirmModal}
                    onClose={() => setShowConfirmModal(false)}
                    title="Refund"
                    message="Are you sure you want to refund order?"
                    handleClick={handleConfirmRefund}
                    buttonLabelSubmit="Confirm"
                    buttonLabelCancel="Cancel"
                    buttonSubmitDisabled={isRefunding}
                    autoCloseAfterSubmit={false}
                />
            </LocalizedStrict>

            {showSuccessModal && (
                <StandardSuccessDialog
                    open={showSuccessModal}
                    dialogType="refundOrder"
                    onClose={() => {
                        setShowSuccessModal(false)
                        onSuccess?.()
                    }}
                />
            )}

            {showErrorDialog.open && (
                <StandardErrorDialog
                    dialogDetails={showErrorDialog}
                    onClose={() => {
                        closeErrorDialog()
                        onSuccess?.()
                    }}
                />
            )}

            {receipt && (
                <ReceiptDialog
                    receipt={receipt}
                    orders={orders}
                    open={!!receipt}
                    onClose={() => setReceipt(undefined)}
                />
            )}

            <LabelPrinterExtensionDialog open={isInstalledError} onClose={onPrinterClose} />
        </>
    )
}
